import BaseAlert from '@/components/base/BaseAlert';
import BaseButton from '@/components/base/BaseButton';
import BaseLoading from '@/components/base/BaseLoading';
import BaseTable, { type BaseTableColumn } from '@/components/base/BaseTable';
import BaseTag from '@/components/base/BaseTag';
import { useIntl } from '@/i18n';
import {
  listAdminAccountLoginLogs,
  listAdminAccountOpLogs,
} from '@/services/iam-admin';
import type { AdminAccountLoginLog, AdminAccountOpLog } from '@/types';
import { localFailureKey, serverMessageOf } from '@/utils/http';
import { UnorderedListOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

interface AdminAccountLogsProps {
  adminId: string;
  kind: 'op' | 'login';
  total: number | null;
  failCount: number | null;
  onClose: () => void;
}

/**
 * 日志页签自己取数：挂载（= 该页签第一次被激活）时读一次，卸载时作废在飞请求。
 * 服务端 `logs: null` 是「日志库读不到」的暂不可判，`[]` 才是没有记录，两者分开呈现。
 */
export default function AdminAccountLogs({
  adminId,
  kind,
  total,
  failCount,
  onClose,
}: AdminAccountLogsProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const t = (key: string, values?: Record<string, unknown>) =>
    intl.formatMessage({ id: `adminAcct.detail.${key}` }, values);
  const [opLogs, setOpLogs] = useState<AdminAccountOpLog[] | null>();
  const [loginLogs, setLoginLogs] = useState<AdminAccountLoginLog[] | null>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    setOpLogs(undefined);
    setLoginLogs(undefined);
    const request =
      kind === 'op'
        ? listAdminAccountOpLogs(adminId, { signal: controller.signal }).then(
            (response) => {
              if (!controller.signal.aborted) setOpLogs(response.logs);
            },
          )
        : listAdminAccountLoginLogs(adminId, {
            signal: controller.signal,
          }).then((response) => {
            if (!controller.signal.aborted) setLoginLogs(response.logs);
          });
    void request
      .catch((reason: unknown) => {
        if (!controller.signal.aborted)
          setError(
            serverMessageOf(reason) ??
              intl.formatMessage({ id: localFailureKey(reason) }),
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [adminId, kind]);
  const rows = kind === 'op' ? opLogs : loginLogs;
  const opColumns: BaseTableColumn<AdminAccountOpLog>[] = [
    {
      title: t('time'),
      dataIndex: 'at',
      valueType: 'dateTime',
      format: 'YYYY-MM-DD HH:mm',
    },
    { title: t('actionType'), dataIndex: 'actionType', width: 150 },
    {
      title: t('target'),
      dataIndex: 'targetName',
      width: 160,
      render: (value) => value ?? '—',
    },
    {
      title: t('change'),
      dataIndex: 'changeSummary',
      render: (value) => (
        <span className="whitespace-pre-wrap break-words">{value ?? '—'}</span>
      ),
    },
  ];
  const loginColumns: BaseTableColumn<AdminAccountLoginLog>[] = [
    {
      title: t('loginTime'),
      dataIndex: 'at',
      valueType: 'dateTime',
      format: 'YYYY-MM-DD HH:mm',
    },
    { title: 'IP', dataIndex: 'ip', width: 150 },
    { title: t('device'), dataIndex: 'device' },
    {
      title: t('result'),
      dataIndex: 'result',
      width: 150,
      render: (value, row) => (
        <>
          <BaseTag color={value === 'success' ? 'green' : 'red'}>
            {t(value === 'success' ? 'success' : 'failure')}
          </BaseTag>
          {row.failReason && (
            <div className="text-xs text-secondary">{row.failReason}</div>
          )}
        </>
      ),
    },
  ];
  if (loading)
    return (
      <div className="relative flex-1 min-h-60">
        <BaseLoading
          overlay
          label={intl.formatMessage({ id: 'common.loading' })}
        />
      </div>
    );
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <BaseAlert
        type="info"
        showIcon
        title={t(kind === 'op' ? 'opNotice' : 'loginNotice')}
        description={
          kind === 'login'
            ? failCount === null
              ? '—'
              : failCount
              ? t('failures', { count: failCount })
              : t('noFailures')
            : undefined
        }
      />
      {error !== undefined || rows === null ? (
        <BaseAlert
          type="error"
          showIcon
          title={error ?? intl.formatMessage({ id: 'http.requestFailed' })}
        />
      ) : kind === 'op' ? (
        <BaseTable
          rowKey={(row, index) => `${row.at}-${index}`}
          columns={opColumns}
          dataSource={opLogs ?? []}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('noOpLogs') }}
        />
      ) : (
        <BaseTable
          rowKey={(row, index) => `${row.at}-${index}`}
          columns={loginColumns}
          dataSource={loginLogs ?? []}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('noLoginLogs') }}
        />
      )}
      {rows && rows.length > 0 && (
        <div className="mt-2 text-xs text-secondary">
          {total !== null &&
            total > rows.length &&
            t('recentCount', { count: rows.length, total })}
          <BaseButton
            icon={<UnorderedListOutlined aria-hidden />}
            type="link"
            onClick={() => {
              onClose();
              navigate(
                kind === 'op'
                  ? `/sys/log-operation?operatorId=${encodeURIComponent(
                      adminId,
                    )}`
                  : `/sys/log-login?adminId=${encodeURIComponent(adminId)}`,
              );
            }}
          >
            {t(kind === 'op' ? 'openOperationLogs' : 'openLoginLogs')}
          </BaseButton>
        </div>
      )}
    </div>
  );
}
